import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../utils/supabaseClient';
import type { ManualEntryAuditChange } from './manualEntryTypes';

interface SupabaseRpcLike {
  rpc<T = unknown>(fn: string, args?: Record<string, unknown>): Promise<{ data: T | null; error: unknown }>;
}

const supabaseRpc = supabase as unknown as SupabaseRpcLike;

interface ManualEntryHistoryProps {
  programId?: string;
}

interface HistoryEntry {
  audit_id: string;
  program_id: string;
  program_name: string | null;
  snapshot_id: string;
  operator_id: string;
  operator_email: string | null;
  action: string;
  changes: ManualEntryAuditChange | null;
  operator_notes: string | null;
  created_at: string;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  return 'just now';
}

export function ManualEntryHistory({ programId }: ManualEntryHistoryProps) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseRpc.rpc('get_manual_entry_history', {
        p_program_id: programId || null,
        p_limit: 50
      });

      if (error) throw error;
      const mapped: HistoryEntry[] = ((data ?? []) as HistoryEntry[]).map((entry) => ({
        audit_id: entry.audit_id,
        program_id: entry.program_id,
        program_name: entry.program_name,
        snapshot_id: entry.snapshot_id,
        operator_id: entry.operator_id,
        operator_email: entry.operator_email,
        action: entry.action,
        changes: entry.changes,
        operator_notes: entry.operator_notes,
        created_at: entry.created_at,
      }));
      setHistory(mapped);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'create': return '#10b981';
      case 'update': return '#3b82f6';
      case 'delete': return '#ef4444';
      case 'review': return '#f59e0b';
      case 'approve': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '32px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid #e5e7eb'
    }}>
      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
        Manual Entry History
      </h3>
      
      {history.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '32px', 
          color: '#6b7280' 
        }}>
          No manual entries yet for this program.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '500' }}>
                  Action
                </th>
                <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '500' }}>
                  Program
                </th>
                <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '500' }}>
                  Operator
                </th>
                <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '500' }}>
                  Details
                </th>
                <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '500' }}>
                  Notes
                </th>
                <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '500' }}>
                  When
                </th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry) => (
                <tr key={entry.audit_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px' }}>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: getActionBadgeColor(entry.action) + '20',
                      color: getActionBadgeColor(entry.action),
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {entry.action}
                    </span>
                  </td>
                  <td style={{ padding: '8px', fontSize: '13px', fontWeight: '500' }}>
                    {entry.program_name ?? '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '13px' }}>
                    {entry.operator_email ?? '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '13px' }}>
                    {entry.changes?.content_length && (
                      <div>Content: {entry.changes.content_length} chars</div>
                    )}
                    {entry.changes?.has_structured_data && (
                      <div>Has structured data</div>
                    )}
                    {entry.changes?.confidence_level && (
                      <div>Confidence: {entry.changes.confidence_level}</div>
                    )}
                  </td>
                  <td style={{ 
                    padding: '8px', 
                    fontSize: '13px',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {entry.operator_notes || '-'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '13px', color: '#6b7280' }}>
                    {formatRelativeTime(entry.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
